import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Music, Upload, X, Clock, AlertCircle } from "lucide-react";

const MAX_DURATION_SECONDS = 300; // 5 minutes
const ALLOWED_TYPES = ["audio/mpeg", "audio/wav", "audio/x-m4a", "audio/mp4"];

export default function AudioUploader({ 
  audioFile, 
  setAudioFile, 
  audioDuration, 
  setAudioDuration 
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const validateAndSetAudio = async (file) => {
    setError(null);
    setIsProcessing(true);

    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Please upload MP3, WAV, or M4A files only");
      setIsProcessing(false);
      return;
    }

    // Check duration using Web Audio API
    try {
      const arrayBuffer = await file.arrayBuffer();
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const duration = audioBuffer.duration;

      if (duration > MAX_DURATION_SECONDS) {
        setError(`Audio must be under 5 minutes. Your file is ${formatDuration(duration)}`);
        setIsProcessing(false);
        return;
      }

      setAudioFile(file);
      setAudioDuration(duration);
    } catch (err) {
      setError("Could not process audio file. Please try another file.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) validateAndSetAudio(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) validateAndSetAudio(file);
  };

  const removeAudio = () => {
    setAudioFile(null);
    setAudioDuration(null);
    setError(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] mb-4">
          <Music className="w-8 h-8 text-purple-400" />
        </div>
        <h2 className="text-2xl font-bold text-[#e0e0e0] mb-2">Upload Your Track</h2>
        <p className="text-[#808080]">
          Add the audio file for your lyric video (max 5 minutes)
        </p>
      </div>

      {/* Upload Area */}
      {!audioFile ? (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onClick={() => fileInputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
            isDragging
              ? "border-purple-500 bg-purple-500/10"
              : "border-[#2a2a2a] hover:border-[#3a3a3a] bg-[#0a0a0a]"
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp3,.wav,.m4a"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Upload className="w-12 h-12 text-[#404040] mx-auto mb-4" />
          <p className="text-[#808080] mb-2">
            {isProcessing ? "Processing..." : "Drag & drop your audio file here"}
          </p>
          <p className="text-sm text-[#505050]">
            or click to browse • MP3, WAV, M4A
          </p>
        </div>
      ) : (
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-lg bg-purple-600/20 flex items-center justify-center">
              <Music className="w-7 h-7 text-purple-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[#e0e0e0] font-medium truncate">{audioFile.name}</p>
              <div className="flex items-center gap-3 text-sm text-[#808080]">
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {formatDuration(audioDuration)}
                </span>
                <span>•</span>
                <span>{(audioFile.size / (1024 * 1024)).toFixed(1)} MB</span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={removeAudio}
              className="text-[#606060] hover:text-red-400 hover:bg-red-500/10"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Tips */}
      <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
        <h4 className="text-sm font-medium text-[#a0a0a0] mb-2">Tips:</h4>
        <ul className="text-xs text-[#606060] space-y-1">
          <li>• Use high-quality audio for the best results</li>
          <li>• Shorter tracks work best (under 3 minutes recommended)</li>
          <li>• Make sure your audio is properly mixed and mastered</li>
        </ul>
      </div>
    </div>
  );
}
